import React, { memo, useCallback, useMemo } from 'react'
import ReactHtmlParser from 'react-html-parser'
import { BsCalendar3 } from 'react-icons/bs'
import { format } from 'date-fns'

import * as styles from './task.module.scss'
import { TaskProps } from './types'
import { Button, Image, Svg } from '../../..'

import InfoTooltip from '../../../infoTooltip/infoTooltip'
import { NotificationPermission, NotificationTypes } from '../../../../../types'

import classNames from 'classnames'
import { TaskTypes } from '../../../../views/tasks/constants'

const Task = ({
   taskType,
   taskDetails,
   onAcceptDeclineClick,
   onCompleteClick,
   onDismissClick,
   onViewNoteClick,
   onViewItemClick,
   isItemsModalTask,
}: TaskProps) => {
   const handleViewItemClick = useCallback(() => {
      if (isItemsModalTask) return null
      const itemData = {
         Barcode: taskDetails.Barcode,
         CustomText1Value: taskDetails.CustomText1,
         Title: taskDetails.ItemName,
         NoteId: taskDetails.ToolNoteId,
         ToolId: taskDetails.ToolId,
         ToolLoanId: taskDetails.ToolLoanId,
         ToolWarehouseId: taskDetails.ToolWarehouseId,
         KitId: taskDetails.KitId,
         NotificationId: taskDetails.Id,
      }
      onViewItemClick(itemData)
   }, [taskDetails.ToolId, isItemsModalTask])

   const handleViewNoteClick = useCallback(() => {
      const itemData = {
         Barcode: taskDetails.Barcode,
         CustomText1Value: taskDetails.CustomText1,
         Title: taskDetails.ItemName,
         NoteId: taskDetails.ToolNoteId,
         ToolId: taskDetails.ToolId,
         ToolLoanId: taskDetails.ToolLoanId,
         ToolWarehouseId: taskDetails.ToolWarehouseId,
      }
      onViewNoteClick(itemData)
   }, [taskDetails])

   const handleOnCompleteClick = useCallback(
      () => onCompleteClick(taskDetails.Id),
      [taskDetails.Id]
   )

   const handleOnDismissClick = useCallback(
      () => onDismissClick(taskDetails.Id),
      [taskDetails.Id]
   )

   const getIconClassName = useCallback((taskType?: string) => {
      if (taskType?.includes('Declined')) {
         return styles.redFill
      } else if (taskType?.includes('Accepted')) {
         return styles.greenFill
      } else {
         return null
      }
   }, [])

   const renderTaskIcon = useCallback((taskType: string) => {
      if (taskType?.includes('Borrow')) {
         return (
            <Svg id="borrowRequest" className={getIconClassName(taskType)} />
         )
      } else if (taskType?.includes('Return')) {
         return (
            <Svg id="returnRequest" className={getIconClassName(taskType)} />
         )
      } else if (taskType?.includes('Transfer')) {
         return (
            <Svg id="transferRequest" className={getIconClassName(taskType)} />
         )
      } else if (
         taskType?.includes('Loan') ||
         taskType?.includes('Connection')
      ) {
         return (
            <Svg
               id="loanRequest"
               stroke="black"
               className={getIconClassName(taskType)}
            />
         )
      } else if (taskType?.includes('Due Back')) {
         return <Svg id="returnRequest" className={styles.orangeFill} />
      } else if (taskType?.includes('Warranty')) {
         return <Svg id="warranty" />
      } else {
         return <Svg id="notepad" />
      }
   }, [])


   // Convert enum flag from API into list of keys for button rendering 
   const getActionKeys = useCallback((value: NotificationPermission): (keyof typeof NotificationPermission)[] => {
      // Get all entries as [key, value]
      const entries = Object.entries(NotificationPermission).filter(([_, v]) => typeof v === "number") as [keyof typeof NotificationPermission, number][];
      
      // If value is exactly 0, return ["None"]
      if (value === NotificationPermission.None) return ["None"];

      // Collect all keys where the bit flag is set
      return entries
         .filter(([_key, bit]) => bit !== 0 && (value & bit) === bit)
         .map(([key]) => key);
   }, [])

   const placeholderParagraph = useMemo(
      () => <p className={styles.placeholder}></p>,
      []
   )

   const actionButtons = useMemo(() => {
      const { ToolId, ToolNoteId, NotificationPermission } = taskDetails
      const actionKeys = getActionKeys(NotificationPermission)
      const buttonWidth = '101px'
      switch (taskType) {
         case NotificationTypes.Action:
            // use of NotificationPermissions to conditionally render buttons added only for Action
            // as there may be custom rendering logic for other types of task
            return (
               <>
                  {actionKeys.includes("Accept") && (
                     <Button
                        type="button"
                        variant="success"
                        className={styles.successButton}
                        onClick={() => onAcceptDeclineClick(taskDetails.Id, true)}
                        minWidth={buttonWidth}
                        small
                     >
                        Accept
                     </Button>
                  )}
                  {actionKeys.includes("Decline") && (
                     <Button
                        type="button"
                        variant="plainV2"
                        className={styles.outlineButton}
                        onClick={() => onAcceptDeclineClick(taskDetails.Id, false)}
                        minWidth={buttonWidth}
                        small
                     >
                        Decline
                     </Button>
                  )}
                  {actionKeys.includes("Delete") && (
                     <Button
                        type="button"
                        variant="plainV2"
                        className={styles.outlineButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleOnDismissClick}
                     >
                        Delete
                     </Button>
                  )}
                  {actionKeys.includes("Dismiss") && (
                     <Button
                        type="button"
                        variant="success"
                        className={styles.successButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleOnDismissClick}
                     >
                        Dismiss
                     </Button>
                  )}
               </>
            )
         case NotificationTypes.Reminder: {
            return (
               <>
                  <Button
                     type="button"
                     variant="success"
                     className={styles.successButton}
                     minWidth={buttonWidth}
                     small
                     onClick={
                        ToolNoteId ? handleViewNoteClick : handleOnCompleteClick
                     }
                  >
                     {ToolNoteId ? 'Complete' : 'Dismiss'}
                  </Button>
                  {Boolean(ToolNoteId) && (
                     <Button
                        type="button"
                        variant="plainV2"
                        className={styles.outlineButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleOnDismissClick}
                     >
                        Dismiss
                     </Button>
                  )}
                  {!ToolNoteId && !isItemsModalTask && (
                     <Button
                        type="button"
                        variant="plainV2"
                        className={styles.outlineButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleViewItemClick}
                     >
                        View Item
                     </Button>
                  )}
               </>
            )
         }
         case NotificationTypes.Info:
            return (
               <>
                  <Button
                     type="button"
                     variant="success"
                     className={styles.successButton}
                     minWidth={buttonWidth}
                     small
                     onClick={handleOnDismissClick}
                  >
                     Dismiss
                  </Button>
                  {Boolean(ToolNoteId) && (
                     <Button
                        type="button"
                        variant="plainV2"
                        className={styles.outlineButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleViewNoteClick}
                     >
                        View Note
                     </Button>
                  )}
                  {Boolean(ToolId) && !ToolNoteId && !isItemsModalTask && (
                     <Button
                        type="button"
                        variant="plainV2"
                        className={styles.outlineButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleViewItemClick}
                     >
                        View Item
                     </Button>
                  )}
               </>
            )
         case NotificationTypes.Global:
            return (
               <>
                  {Boolean(ToolNoteId) && (
                     <Button
                        type="button"
                        variant="success"
                        className={styles.successButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleViewNoteClick}
                     >
                        View Note
                     </Button>
                  )}
                  {Boolean(ToolId) && !ToolNoteId && !isItemsModalTask && (
                     <Button
                        type="button"
                        variant="success"
                        className={styles.successButton}
                        minWidth={buttonWidth}
                        small
                        onClick={handleViewItemClick}
                     >
                        View Item
                     </Button>
                  )}
                  <Button
                     type="button"
                     variant="plainV2"
                     className={styles.outlineButton}
                     minWidth={buttonWidth}
                     small
                     onClick={handleOnDismissClick}
                  >
                     Delete
                  </Button>
               </>
            )
      }
   }, [taskType, taskDetails, isItemsModalTask])

   const renderBoldLabel = useCallback(
      (text?: string) =>
         text ? (
            <>
               <b>{text}</b>&nbsp;
            </>
         ) : (
            <></>
         ),
      []
   )

   const taskDetailsClassName = useMemo(
      () =>
         classNames(styles.taskDetails, {
            [styles.width35]: isItemsModalTask,
         }),
      [isItemsModalTask]
   )

   const itemDetailsClassName = useMemo(
      () =>
         classNames(styles.itemDetails, {
            [styles.width42]: isItemsModalTask,
         }),
      [isItemsModalTask]
   )

   const itemInfoClassName = useMemo(
      () => (isItemsModalTask ? styles.itemsTabItemInfo : styles.itemInfo),
      [isItemsModalTask]
   )

   return (
      <article className={styles.task}>
         {taskDetails.Type !== TaskTypes.SmartAuditNotification ? (
            <div className={styles.taskDetails}>
               <div className={styles.taskHeaderContainer}>
                  <div className={styles.iconAndTitle}>
                     {renderTaskIcon(taskDetails?.NotificationTitle)}
                     <div className={styles.headingContainer}>
                        <h3>{ReactHtmlParser(taskDetails?.NotificationTitle)}</h3>
                     </div>
                  </div>
                  {taskDetails.Comments && (
                     <div className={styles.tooltipSpacer}>
                        <InfoTooltip
                           openIcon="comment-filled"
                           closedIcon="comment-outlined"
                           content={taskDetails.Comments} 
                           ariaLabel={'Action Comments'}
                           inlineStyle={{ 'left': '0px' }}
                        />
                     </div>
                  )}
               </div>
               <div className={styles.detailsSection}>
                  {taskDetails?.UserName1 ? (
                     <p>
                        <Image
                           className={styles.detailsImage}
                           width={12}
                           url={taskDetails?.UserImage1}
                           alt={taskDetails.UserName1}
                        />
                        {renderBoldLabel(taskDetails?.UserInitial1)}
                        {taskDetails.UserName1}
                     </p>
                  ) : (
                     placeholderParagraph
                  )}
                  {taskDetails?.DestinationLocation ? (
                     <p title="Location">
                        <Svg
                           id="taskLocation"
                           className={styles.iconLeftPadding}
                        />
                        {renderBoldLabel(taskDetails?.DestinationLocationInitial)}
                        {taskDetails.DestinationLocation}
                     </p>
                  ) : (
                     placeholderParagraph
                  )}
                  {taskDetails?.DateCreated ? (
                     <p>
                        <BsCalendar3 />
                        {renderBoldLabel(taskDetails?.DateCreatedInitial)}
                        {format(new Date(taskDetails.DateCreated), 'd MMM yyyy')}
                     </p>
                  ) : (
                     placeholderParagraph
                  )}
               </div>
            </div>
         ) : (
            <div className={styles.taskDetailsSmartAuditContainer}>
               <div className={styles.taskDetailsSmartAudit}>
                  <div className={styles.iconAndTitle}>
                     {renderTaskIcon(taskDetails?.NotificationTitle)}
                     <h3>{taskDetails.Message}</h3> 
                  </div>

               </div>
               <div className={`
                  ${styles.detailsSection}
                  ${styles.detailSectionSize}
               `}>
                  {taskDetails?.DateCreated ? (
                     <p>
                        <BsCalendar3 />
                        {renderBoldLabel(taskDetails?.DateCreatedInitial)}
                        {format(new Date(taskDetails.DateCreated), 'd MMM yyyy')}
                     </p>
                  ) : (
                     placeholderParagraph
                  )}
               </div>
            </div>
         )}
         {taskDetails?.ToolId && (
            <div className={itemDetailsClassName}>
               {!isItemsModalTask && (
                  <span
                     className={styles.itemImage}
                     onClick={handleViewItemClick}
                  >
                     <Image
                        padding={5}
                        url={taskDetails?.ImageUrl}
                        alt={taskDetails?.ItemName}
                        width={96}
                        height={96}
                     />
                  </span>
               )}
               <div className={itemInfoClassName}>
                  <div className={styles.headingContainer}>
                     <h3 onClick={handleViewItemClick}>
                        <span>
                           {taskDetails?.ItemName}
                           {!isItemsModalTask && <Svg id="expand" />}
                        </span>
                        {taskDetails?.Quantity > 1 && (
                           <span>QTY:&nbsp;{taskDetails.Quantity}</span>
                        )}
                     </h3>
                  </div>
                  <div className={styles.detailsSection}>
                     {taskDetails?.UserName2 ? (
                        <p>
                           <Image
                              className={styles.detailsImage}
                              width={12}
                              url={taskDetails?.UserImage2}
                              alt={taskDetails.UserName2}
                           />
                           {renderBoldLabel(taskDetails?.UserInitial2)}
                           {taskDetails.UserName2}
                        </p>
                     ) : (
                        placeholderParagraph
                     )}
                     {taskDetails?.SourceLocation ||
                     (taskDetails?.CustomText1 && taskDetails?.SerialNumber) ? (
                        <p className={styles.itemDetailsParagraph}>
                           {taskDetails?.SourceLocation && (
                              <span
                                 title={
                                    taskDetails?.SourceLocationInitial === 'For'
                                       ? 'Current Location'
                                       : 'Warehouse'
                                 }
                              >
                                 <Svg
                                    id={
                                       taskDetails?.SourceLocationInitial ===
                                       'For'
                                          ? 'taskLocation'
                                          : 'taskWarehouse'
                                    }
                                    className={
                                       taskDetails?.SourceLocationInitial ===
                                       'For'
                                          ? styles.iconLeftPadding
                                          : ''
                                    }
                                 />
                                 {renderBoldLabel(
                                    taskDetails?.SourceLocationInitial
                                 )}
                                 {taskDetails.SourceLocation}
                              </span>
                           )}
                           {taskDetails?.CustomText1 &&
                              taskDetails?.SerialNumber && (
                                 <span title="Custom Text">
                                    <Svg
                                       id="taskCustomField"
                                       className={styles.iconTopPadding}
                                    />
                                    {taskDetails.CustomText1}
                                 </span>
                              )}
                        </p>
                     ) : (
                        placeholderParagraph
                     )}
                     {taskDetails?.Barcode ||
                     taskDetails?.SerialNumber ||
                     taskDetails?.CustomText1 ? (
                        <p className={styles.itemDetailsParagraph}>
                           {taskDetails?.Barcode && (
                              <span title="Barcode">
                                 <Svg id="taskBarcode" />
                                 {taskDetails.Barcode}
                              </span>
                           )}
                           {taskDetails?.SerialNumber ? (
                              <span title="Serial Number">
                                 <Svg
                                    id="cardViewSerialNumber"
                                    className={styles.serialNumberIcon}
                                    overrideViewBox="0 0 9 9"
                                 />
                                 {taskDetails.SerialNumber}
                              </span>
                           ) : taskDetails?.CustomText1 ? (
                              <span title="Custom Text">
                                 <Svg
                                    id="taskCustomField"
                                    className={styles.iconTopPadding}
                                 />
                                 {taskDetails.CustomText1}
                              </span>
                           ) : (
                              <></>
                           )}
                        </p>
                     ) : (
                        placeholderParagraph
                     )}
                  </div>
               </div>
            </div>
         )}
         <div className={styles.actionButtons}>{actionButtons}</div>
      </article>
   )
}

export default memo(Task)
